import { useEffect, useMemo, useState } from 'react';

import { loadEvents } from '../api/events';
import { useAuth } from '../state/AuthProvider';
import { Shell } from '../ui/PwaContent';
import { CalendarEventCard } from './CalendarEventCard';
import { CalendarEvent } from './calendarTypes';
import { getTodayKey, groupEventsByMonth, shouldShowEvent } from './calendarUtils';

export function CalendarAgendaView() {
    const authContext = useAuth();

    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showPastCurrentMonth, setShowPastCurrentMonth] = useState(false);
    const [showPastMonths, setShowPastMonths] = useState(false);

    useEffect(() => {
        let cancelled = false;

        async function loadCalendarEvents() {
            setLoading(true);
            setError(null);

            try {
                const result = await loadEvents(authContext.apiBaseUrl);

                if (!cancelled) {
                    setEvents(result);
                }
            } catch (loadError) {
                if (!cancelled) {
                    setError(
                        loadError instanceof Error
                            ? loadError.message
                            : 'Termine konnten nicht geladen werden.',
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        void loadCalendarEvents();

        return () => {
            cancelled = true;
        };
    }, [authContext.apiBaseUrl]);

    const todayKey = getTodayKey();

    const hiddenPastCurrentMonthCount = useMemo(() => {
        return events.filter((event) => {
            return !shouldShowEvent(event, true, false, todayKey)
                && shouldShowEvent(event, true, true, todayKey);
        }).length;
    }, [events, todayKey]);

    const hiddenPastMonthCount = useMemo(() => {
        return events.filter((event) => {
            return !shouldShowEvent(event, false, false, todayKey)
                && !shouldShowEvent(event, true, false, todayKey);
        }).length;
    }, [events, todayKey]);

    const visibleEvents = useMemo(() => {
        return events.filter((event) => shouldShowEvent(event, showPastCurrentMonth, showPastMonths, todayKey));
    }, [events, showPastCurrentMonth, showPastMonths, todayKey]);

    const monthGroups = useMemo(() => groupEventsByMonth(visibleEvents), [visibleEvents]);

    function handleRespond(event: CalendarEvent, response: 'yes' | 'maybe' | 'no') {
        // Der API-Endpunkt ist bereits vorbereitet. Die echte Speicherung hängen wir im nächsten Schritt an.
        console.info('Kalenderantwort', {
            eventId: event.id,
            response,
        });
    }

    if (loading) {
        return (
            <Shell title="Kalender" subtitle="Termine werden geladen ...">
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-gray-600">
                    Kalenderdaten werden aus der Plus-API geladen.
                </div>
            </Shell>
        );
    }

    if (error) {
        return (
            <Shell title="Kalender" subtitle="Fehler beim Laden der Termine">
                <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-900">
                    {error}
                </div>
            </Shell>
        );
    }

    return (
        <Shell title="Kalender" subtitle={`${visibleEvents.length} sichtbare Termine`}>
            <div className="mb-5 flex flex-wrap gap-2 print:hidden">
                {hiddenPastCurrentMonthCount > 0 && (
                    <button
                        type="button"
                        className={showPastCurrentMonth ? 'wwpwa-button-primary' : 'wwpwa-button-secondary'}
                        onClick={() => setShowPastCurrentMonth((value) => !value)}
                    >
                        {showPastCurrentMonth ? 'Vergangene Termine im aktuellen Monat ausblenden' : 'Vergangene Termine im aktuellen Monat anzeigen'}
                    </button>
                )}

                {hiddenPastMonthCount > 0 && (
                    <button
                        type="button"
                        className={showPastMonths ? 'wwpwa-button-primary' : 'wwpwa-button-secondary'}
                        onClick={() => setShowPastMonths((value) => !value)}
                    >
                        {showPastMonths ? 'Vergangene Monate ausblenden' : 'Vergangene Monate anzeigen'}
                    </button>
                )}

                <a
                    className="rounded-xl border border-gray-200 bg-white px-4 py-2 font-bold text-gray-700 hover:bg-gray-50"
                    href="/kalender/druck/"
                >
                    Halbjahr drucken
                </a>
            </div>

            <div className="grid gap-8">
                {monthGroups.map((month) => (
                    <section key={month.key} className="grid gap-3">
                        <h2 className="border-b border-gray-200 pb-2 text-2xl font-bold text-gray-950">
                            {month.label}
                        </h2>

                        <div className="grid gap-3">
                            {month.events.map((event) => (
                                <CalendarEventCard
                                    key={event.id}
                                    event={event}
                                    onRespond={handleRespond}
                                />
                            ))}
                        </div>
                    </section>
                ))}

                {monthGroups.length === 0 && (
                    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-gray-600">
                        Aktuell sind keine sichtbaren Termine vorhanden.
                    </div>
                )}
            </div>
        </Shell>
    );
}
